import type { MetroMapEvent, MetroMapState, Store } from '../state/metroMapStore'
import { isRoutePage } from './promptSurface'

/**
 * The destination of the route a route page shows, once it is shown.
 *
 * A route page loads its route after the page itself (URL → store →
 * routing), so this waits for the route to settle: `results` → the To
 * station's name; `empty` / `error`, a page that is not a route page, or no
 * answer within `timeoutMs` → `undefined`, and the visitor gets the home
 * prompt instead of a route prompt about a route they never saw.
 */
export function routeDestination(
  store: Store<MetroMapState, MetroMapEvent>,
  entryPathname: string,
  timeoutMs = 5_000,
): Promise<string | undefined> {
  if (!isRoutePage(entryPathname)) return Promise.resolve(undefined)

  const settle = (state: MetroMapState): { done: boolean; destination?: string } => {
    switch (state.routeStatus.kind) {
      case 'results':
        return { done: true, destination: state.routeSelection.toStation?.localName }
      case 'empty':
      case 'error':
        return { done: true }
      default:
        return { done: false }
    }
  }

  return new Promise((resolve) => {
    const first = settle(store.getState())
    if (first.done) {
      resolve(first.destination)
      return
    }
    const finish = (destination: string | undefined) => {
      clearTimeout(timer)
      unsubscribe()
      resolve(destination)
    }
    const unsubscribe = store.subscribe(() => {
      const next = settle(store.getState())
      if (next.done) finish(next.destination)
    })
    const timer = setTimeout(() => finish(undefined), timeoutMs)
  })
}
